import React, { useEffect, useMemo, useState } from "react";
import { FaPause, FaPlay, FaVolumeUp } from "react-icons/fa";

import { getSettings } from "./../services/settingsServices";
import Reveal from "./../components/ui/Reveal";

const FALLBACK_TITLE = "À propos de notre commune";
const FALLBACK_DESC =
  "Cette section permet de présenter la commune, son histoire, ses services et la vie locale.";
const FALLBACK_IMAGE = "/img/about-fallback.jpg";

const safeStr = (value) => (typeof value === "string" ? value.trim() : "");

function AudioReadButton({ title, description }) {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const canSpeak =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    typeof window.SpeechSynthesisUtterance !== "undefined";

  useEffect(() => {
    return () => {
      if (canSpeak) {
        window.speechSynthesis.cancel();
      }
    };
  }, [canSpeak]);

  const handleRead = () => {
    if (!canSpeak) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const textToRead = [title, description].filter(Boolean).join(". ");

    if (!textToRead.trim()) return;

    const utterance = new window.SpeechSynthesisUtterance(textToRead);
    utterance.lang = "fr-FR";
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };

  if (!canSpeak) return null;

  return (
    <button
      type="button"
      onClick={handleRead}
      className="group inline-flex min-h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-200"
      aria-label={
        isSpeaking
          ? "Arrêter la lecture audio"
          : "Lire la présentation de la commune"
      }
      title={
        isSpeaking
          ? "Arrêter la lecture audio"
          : "Lire la présentation de la commune"
      }
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-700 transition group-hover:bg-sky-600 group-hover:text-white dark:bg-sky-500/15 dark:text-sky-300">
        {isSpeaking ? (
          <FaPause className="h-3 w-3" />
        ) : (
          <FaPlay className="h-3 w-3 translate-x-[1px]" />
        )}
      </span>

      <span className="hidden sm:inline">
        {isSpeaking ? "Arrêter" : "Écouter"}
      </span>

      <FaVolumeUp className="hidden h-3.5 w-3.5 opacity-60 md:block" />
    </button>
  );
}

export default function AboutSection() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (!mounted) return;

        const currentSettings = Array.isArray(data) ? data[0] : data;
        setSettings(currentSettings || null);
      } catch (error) {
        console.error("Erreur chargement settings pour AboutSection:", error);

        if (mounted) {
          setSettings(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const computed = useMemo(() => {
    const titre = safeStr(settings?.titre_about) || FALLBACK_TITLE;
    const description = safeStr(settings?.description_about) || FALLBACK_DESC;

    const logo =
      safeStr(settings?.logo_mairie) ||
      safeStr(settings?.photoURL_about) ||
      safeStr(settings?.photoURL1) ||
      FALLBACK_IMAGE;

    const nomMairie =
      safeStr(settings?.nom_mairie) ||
      safeStr(settings?.ville) ||
      "Votre mairie";

    const primaryColor = safeStr(settings?.primary_color) || "#0ea5e9";

    return {
      titre,
      description,
      logo,
      nomMairie,
      primaryColor,
    };
  }, [settings]);

  if (loading) return null;
  if (!computed.titre && !computed.description) return null;

  return (
    <section
      id="a-propos"
      aria-labelledby="about-title"
      className="relative overflow-hidden bg-slate-50 px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      itemScope
      itemType="https://schema.org/GovernmentOrganization"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute right-0 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent dark:via-white/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.18fr)_minmax(240px,0.82fr)] lg:gap-16">
          <div className="relative">
            <div
              className="absolute -left-5 top-16 hidden h-32 w-[5px] rounded-full lg:block"
              style={{ backgroundColor: computed.primaryColor }}
              aria-hidden="true"
            />

            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="inline-flex min-h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: computed.primaryColor }}
                  />
                  À propos de la commune
                </div>

                <AudioReadButton
                  title={computed.titre}
                  description={computed.description}
                />
              </div>
            </Reveal>

            <Reveal>
              <h2
                id="about-title"
                className="max-w-4xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
                itemProp="name"
              >
                {computed.titre}
              </h2>
            </Reveal>

            <Reveal>
              <p
                className="mt-6 max-w-4xl whitespace-pre-line text-base font-medium leading-8 text-slate-600 dark:text-slate-300 md:text-base lg:text-lg"
                itemProp="description"
              >
                {computed.description}
              </p>
            </Reveal>
          </div>

          <Reveal>
            <div className="relative flex items-center justify-center lg:justify-end">
              <div
                className="absolute h-64 w-64 rounded-full opacity-20 blur-3xl"
                style={{ backgroundColor: computed.primaryColor }}
                aria-hidden="true"
              />

              <img
                src={computed.logo}
                alt={`Logo ${computed.nomMairie}`}
                className="relative max-h-[240px] w-auto object-contain md:max-h-[300px] lg:max-h-[340px]"
                loading="lazy"
                itemProp="image"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}